import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import * as s from './styled-station-form';
import {
  removeInvalidChars,
  justNumbers,
  justNumbersObject,
  notifyError,
} from '../../utils';
import { yupMessages } from '../../constants';

// Icone X inline (lucide-style). Stroke 2, currentColor — herda do botao
// quando hover muda pra red.
const IX = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

// Resolve {id} | {value} | string -> string id puro. listZona/listCustomer
// chegam como [{label, value}], mas formikInitialValues.zona pode vir
// como objeto {id} (legacy) ou string id.
const pickId = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.id || v.value || '';
  return '';
};

export default ({
  id = 'estacao',
  onClose,
  onCloseConfirmation,
  submit,
  title,
  dadosAbaixo,
  sucesso,
  iconFinal,
  estacaoConcluido,
  setConfirmationModal,
  setEstacaoConcluido,
  listCustomer = [],
  listZona = [],
  labelButton,
  msgButton,
  formikInitialValues,
  setConfirmationMaquinaModal,
}) => {
  const [cepValid, setCepValid] = useState(false);

  const initialValues = useMemo(
    () => ({
      name: formikInitialValues.name || '',
      cep: formikInitialValues.cep || '',
      estado: formikInitialValues.estado || '',
      cidade: formikInitialValues.cidade || '',
      endereco: formikInitialValues.endereco || '',
      bairro: formikInitialValues.bairro || '',
      numero: formikInitialValues.numero || '',
      latitude: formikInitialValues.latitude || '',
      longitude: formikInitialValues.longitude || '',
      zona: pickId(formikInitialValues.zona),
      customer: pickId(formikInitialValues.customer),
    }),
    [formikInitialValues],
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().required(yupMessages.REQUIRED),
      cep: yup.string().required(yupMessages.REQUIRED),
      estado: yup.string().required(yupMessages.REQUIRED),
      cidade: yup.string().required(yupMessages.REQUIRED),
      endereco: yup.string().required(yupMessages.REQUIRED),
      bairro: yup.string().required(yupMessages.REQUIRED),
      numero: yup.string().required(yupMessages.REQUIRED),
      latitude: yup.string().required(yupMessages.REQUIRED),
      longitude: yup.string().required(yupMessages.REQUIRED),
      zona: yup.string().required(yupMessages.REQUIRED),
      customer: yup.string().required(yupMessages.REQUIRED),
    }),
    onSubmit: () => {
      // Submit real e disparado via onSubmit do form (handleSubmit chama isto).
    },
  });

  // Busca via CEP — preenche estado/cidade/endereco/bairro automaticamente.
  // Mantido o endpoint publico (viacep) ja em uso pelo projeto.
  const validateCep = useCallback(
    (cep) => {
      fetch(`https://viacep.com.br/ws/${cep}/json/`)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.erro) {
            notifyError('CEP inválido');
            setCepValid(false);
            formik.setFieldValue('cidade', '');
            formik.setFieldValue('estado', '');
            formik.setFieldValue('endereco', '');
            formik.setFieldValue('bairro', '');
          } else {
            setCepValid(true);
            formik.setFieldValue('cidade', data.localidade || '');
            formik.setFieldValue('estado', data.uf || '');
            formik.setFieldValue('endereco', data.logradouro || '');
            formik.setFieldValue('bairro', data.bairro || '');
          }
        })
        .catch(() => {
          setCepValid(false);
        });
    },
    // formik.setFieldValue e estavel; nao queremos re-rodar a cada keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Em edicao consideramos o CEP ja preenchido como valido por default.
  useEffect(() => {
    if (justNumbers(initialValues.cep).length === 8) {
      setCepValid(true);
    }
  }, [initialValues.cep]);

  useEffect(() => {
    if (justNumbers(formik.values.cep).length === 8) {
      validateCep(justNumbers(formik.values.cep));
    } else {
      setCepValid(false);
    }
    // Apenas reagir quando o CEP muda — validateCep e estavel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.cep]);

  const unfilled =
    !formik.values.name ||
    !(justNumbers(formik.values.cep).length === 8) ||
    !formik.values.endereco ||
    !formik.values.estado ||
    !formik.values.cidade ||
    !formik.values.bairro ||
    !formik.values.numero ||
    !formik.values.latitude ||
    !formik.values.longitude ||
    !formik.values.zona ||
    !formik.values.customer ||
    !cepValid;

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (unfilled) {
      notifyError('Preencha todos os campos obrigatórios');
      return;
    }
    if (justNumbers(formik.values.cep).length !== 8) {
      notifyError('CEP não está completo');
      return;
    }
    if (setConfirmationModal) setConfirmationModal(true);
    submit && submit(formik.values);
  };

  // Click no scrim fecha — replicar comportamento do design.
  const handleScrimClick = (e) => {
    if (e.target.dataset && e.target.dataset.scrim === 'true') {
      if (onCloseConfirmation) onCloseConfirmation(true);
      else if (onClose) onClose();
    }
  };

  const handleClose = () => {
    if (onCloseConfirmation) onCloseConfirmation(true);
    else if (onClose) onClose();
  };

  // --- Tela de sucesso ---------------------------------------------------
  if (estacaoConcluido) {
    return (
      <s.Scrim
        id={id}
        data-scrim="true"
        onClick={handleScrimClick}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <s.Modal>
          <s.ModalHead>
            <div>
              <s.Title>{title}</s.Title>
            </div>
            <s.Close type="button" onClick={handleClose} aria-label="Fechar">
              {IX}
            </s.Close>
          </s.ModalHead>
          <s.SuccessBody>
            {iconFinal && <s.SuccessIcon src={iconFinal} alt="" />}
            <s.SuccessMsg>{sucesso}</s.SuccessMsg>
          </s.SuccessBody>
          <s.ModalFoot>
            <s.SaveBtn
              type="button"
              onClick={() => {
                if (onClose) onClose();
                if (setEstacaoConcluido) setEstacaoConcluido(false);
                if (setConfirmationMaquinaModal) {
                  setConfirmationMaquinaModal(true);
                }
              }}
            >
              Concluir
            </s.SaveBtn>
          </s.ModalFoot>
        </s.Modal>
      </s.Scrim>
    );
  }

  // --- Formulario --------------------------------------------------------
  return (
    <s.Scrim
      id={id}
      data-scrim="true"
      onClick={handleScrimClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <s.Modal>
        <s.ModalHead>
          <div>
            <s.Title>{title}</s.Title>
            {dadosAbaixo && <s.Desc>{dadosAbaixo}</s.Desc>}
            <s.ReqHint>
              <span className="req">*</span>Itens obrigatórios
            </s.ReqHint>
          </div>
          <s.Close type="button" onClick={handleClose} aria-label="Fechar">
            {IX}
          </s.Close>
        </s.ModalHead>

        <s.ModalBody>
          <s.Form id={`${id}-form`} onSubmit={handleSubmit} noValidate>
            <s.FormGrid>
              {/* Nome — c12 (full row) */}
              <s.Field $cols={12}>
                <s.Label htmlFor={`${id}-name`}>
                  Nome da Estação <span className="req">*</span>
                </s.Label>
                <s.Input
                  id={`${id}-name`}
                  name="name"
                  placeholder="Ex.: Campo Belo"
                  maxLength={50}
                  value={formik.values.name}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.name && !!formik.errors.name}
                />
                {formik.touched.name && formik.errors.name && (
                  <s.ErrorMsg>{formik.errors.name}</s.ErrorMsg>
                )}
              </s.Field>

              {/* CEP — c4 */}
              <s.Field $cols={4}>
                <s.Label htmlFor={`${id}-cep`}>
                  CEP <span className="req">*</span>
                </s.Label>
                <s.MaskedInput
                  id={`${id}-cep`}
                  name="cep"
                  mask="99999-999"
                  maskChar={null}
                  placeholder="00000-000"
                  value={formik.values.cep}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.cep && !!formik.errors.cep}
                />
                {formik.touched.cep && formik.errors.cep && (
                  <s.ErrorMsg>{formik.errors.cep}</s.ErrorMsg>
                )}
              </s.Field>

              {/* Estado — c4 (disabled, preenchido via CEP) */}
              <s.Field $cols={4}>
                <s.Label htmlFor={`${id}-estado`}>
                  Estado <span className="req">*</span>
                </s.Label>
                <s.Input
                  id={`${id}-estado`}
                  name="estado"
                  placeholder="UF"
                  maxLength={2}
                  value={formik.values.estado}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  disabled
                  $error={formik.touched.estado && !!formik.errors.estado}
                />
              </s.Field>

              {/* Cidade — c4 (disabled, preenchido via CEP) */}
              <s.Field $cols={4}>
                <s.Label htmlFor={`${id}-cidade`}>
                  Cidade <span className="req">*</span>
                </s.Label>
                <s.Input
                  id={`${id}-cidade`}
                  name="cidade"
                  placeholder="Cidade"
                  value={formik.values.cidade}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  disabled
                  $error={formik.touched.cidade && !!formik.errors.cidade}
                />
              </s.Field>

              {/* Endereco — c8 */}
              <s.Field $cols={8}>
                <s.Label htmlFor={`${id}-endereco`}>
                  Endereço <span className="req">*</span>
                </s.Label>
                <s.Input
                  id={`${id}-endereco`}
                  name="endereco"
                  placeholder="Logradouro"
                  maxLength={120}
                  value={formik.values.endereco}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.endereco && !!formik.errors.endereco}
                />
                {formik.touched.endereco && formik.errors.endereco && (
                  <s.ErrorMsg>{formik.errors.endereco}</s.ErrorMsg>
                )}
              </s.Field>

              {/* Numero — c4 */}
              <s.Field $cols={4}>
                <s.Label htmlFor={`${id}-numero`}>
                  Número <span className="req">*</span>
                </s.Label>
                <s.Input
                  id={`${id}-numero`}
                  name="numero"
                  placeholder="Nº ou km"
                  maxLength={10}
                  value={formik.values.numero}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.numero && !!formik.errors.numero}
                />
                {formik.touched.numero && formik.errors.numero && (
                  <s.ErrorMsg>{formik.errors.numero}</s.ErrorMsg>
                )}
              </s.Field>

              {/* Bairro — c6 */}
              <s.Field $cols={6}>
                <s.Label htmlFor={`${id}-bairro`}>
                  Bairro <span className="req">*</span>
                </s.Label>
                <s.Input
                  id={`${id}-bairro`}
                  name="bairro"
                  placeholder="Bairro"
                  maxLength={50}
                  value={formik.values.bairro}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.bairro && !!formik.errors.bairro}
                />
                {formik.touched.bairro && formik.errors.bairro && (
                  <s.ErrorMsg>{formik.errors.bairro}</s.ErrorMsg>
                )}
              </s.Field>

              {/* Zona — c6 (native select) */}
              <s.Field $cols={6}>
                <s.Label htmlFor={`${id}-zona`}>
                  Zona <span className="req">*</span>
                </s.Label>
                <s.Select
                  id={`${id}-zona`}
                  name="zona"
                  value={formik.values.zona}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.zona && !!formik.errors.zona}
                >
                  <option value="">Selecione</option>
                  {listZona.map((z) => (
                    <option key={z.value} value={z.value}>
                      {z.label}
                    </option>
                  ))}
                </s.Select>
                {formik.touched.zona && formik.errors.zona && (
                  <s.ErrorMsg>{formik.errors.zona}</s.ErrorMsg>
                )}
              </s.Field>

              {/* Latitude — c6 (mono) */}
              <s.Field $cols={6}>
                <s.Label htmlFor={`${id}-latitude`}>
                  Latitude <span className="req">*</span>
                </s.Label>
                <s.Input
                  id={`${id}-latitude`}
                  name="latitude"
                  placeholder="-00.000000"
                  value={formik.values.latitude}
                  onChange={(e) =>
                    formik.handleChange(
                      justNumbersObject(e, formik.values.latitude, 'latitude'),
                    )
                  }
                  onBlur={formik.handleBlur}
                  $mono
                  $error={formik.touched.latitude && !!formik.errors.latitude}
                />
                {formik.touched.latitude && formik.errors.latitude && (
                  <s.ErrorMsg>{formik.errors.latitude}</s.ErrorMsg>
                )}
              </s.Field>

              {/* Longitude — c6 (mono) */}
              <s.Field $cols={6}>
                <s.Label htmlFor={`${id}-longitude`}>
                  Longitude <span className="req">*</span>
                </s.Label>
                <s.Input
                  id={`${id}-longitude`}
                  name="longitude"
                  placeholder="-00.000000"
                  value={formik.values.longitude}
                  onChange={(e) =>
                    formik.handleChange(
                      justNumbersObject(
                        e,
                        formik.values.longitude,
                        'longitude',
                      ),
                    )
                  }
                  onBlur={formik.handleBlur}
                  $mono
                  $error={
                    formik.touched.longitude && !!formik.errors.longitude
                  }
                />
                {formik.touched.longitude && formik.errors.longitude && (
                  <s.ErrorMsg>{formik.errors.longitude}</s.ErrorMsg>
                )}
              </s.Field>

              {/* Cliente — c12 (full row pra label longa) */}
              <s.Field $cols={12}>
                <s.Label htmlFor={`${id}-customer`}>
                  Vincular Cliente <span className="req">*</span>
                </s.Label>
                <s.Select
                  id={`${id}-customer`}
                  name="customer"
                  value={formik.values.customer}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  $error={
                    formik.touched.customer && !!formik.errors.customer
                  }
                >
                  <option value="">Selecione um cliente vinculado</option>
                  {listCustomer.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </s.Select>
                {formik.touched.customer && formik.errors.customer && (
                  <s.ErrorMsg>{formik.errors.customer}</s.ErrorMsg>
                )}
              </s.Field>
            </s.FormGrid>
          </s.Form>
        </s.ModalBody>

        <s.ModalFoot>
          {msgButton && <s.ConfirmHint>{msgButton}</s.ConfirmHint>}
          <s.CancelBtn type="button" onClick={handleClose}>
            Cancelar
          </s.CancelBtn>
          <s.SaveBtn
            type="submit"
            form={`${id}-form`}
            disabled={unfilled}
            onClick={handleSubmit}
          >
            {labelButton}
          </s.SaveBtn>
        </s.ModalFoot>
      </s.Modal>
    </s.Scrim>
  );
};
